'use client';

import { useRef, useState, useSyncExternalStore } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight } from '@phosphor-icons/react';
import { PlusIcon } from '@/components/icons';
import { api } from '@/lib/api';
import { normalizePromotions, type PublicPromotion } from '@/lib/promotion-content';
import { Header } from '@/components/layout/header';
import { Footer } from '@/components/layout/footer';
import { OfferDialog } from './offer-dialog';
import { OfferMenuInvitation } from './offer-menu-invitation';
import {
  CompactOffersIntro,
  IntroOptionsToolbar,
  OfferIntroOption,
  type IntroOption,
} from './intro-options';
import { useMoneyRules } from '@/lib/money-rules';
import { useStatusRolls } from '@/hooks/use-status-rolls';
import type { ProductWithCategories } from '@/lib/api/products';
import { usePhotoEdges } from './offer-product';
import { getOfferCampaign, getOfferPhoto } from './offer-photo-source';
import { OfferCatalogPhoto, useOfferGift } from './offer-catalog-photo';
import { MobileOffersExperience, type OfferPreviewSelection } from './mobile-offers-experience';
import '../storefront/storefront.css';
import '../storefront/integration.css';
import './actions-experience.css';

const subscribeHydration = () => () => {};

const detailKeys = new Set(['firstOrder', 'statusRolls', 'review', 'birthday', 'loyalty']);

function hasDetails(key: string | null) {
  return key !== null && detailKeys.has(key);
}

const photoKeys: Record<string, string> = {
  first_order: 'first-order',
  status_rolls: 'status',
  review: 'review',
  loyalty: 'loyalty',
  birthday: 'first-order',
};

const offerActionLabels: Record<string, string> = {
  firstOrder: 'Как получить подарок',
  statusRolls: 'Посмотреть подарки',
  review: 'Об акции за отзыв',
  loyalty: 'Уровни и коины',
  birthday: 'Забрать промокод',
};

function photoFor(detailKey: string | null) {
  // Information-only and unrecognised offers must never imply a fictional gift.
  return detailKey === 'review'
    ? '/storefront/offers-v2-review.webp'
    : '/storefront/offers-v2-loyalty.webp';
}

// Food artwork follows the configured gift and its approved photographic scene.
// An admin photo replacement immediately invalidates the corresponding local scene.
function CatalogCardArt({
  product,
  loading,
  eager,
}: {
  product?: ProductWithCategories;
  loading: boolean;
  eager?: boolean;
}) {
  // Shared gift queries can resolve before this streamed subtree hydrates.
  // Keep its first client artwork identical to the server loading placeholder.
  const hydrated = useSyncExternalStore(subscribeHydration, () => true, () => false);
  const displayProduct = hydrated ? product : undefined;
  const [failedCover, setFailedCover] = useState<string | null>(null);
  const approvedCover = getOfferCampaign(displayProduct)?.desktop;
  const cover = approvedCover !== failedCover ? approvedCover : undefined;
  const scene = usePhotoEdges(cover ? undefined : getOfferPhoto(displayProduct));
  if (cover) {
    return (
      <div
        className="offer-card-art offer-card-art--campaign"
        data-campaign-product-id={displayProduct?.id}
        aria-hidden="true"
      >
        <picture>
          <img
            src={cover}
            alt=""
            width={1536}
            height={1024}
            loading={eager ? 'eager' : 'lazy'}
            fetchPriority={eager ? 'high' : 'auto'}
            decoding="async"
            onError={() => setFailedCover(cover)}
          />
        </picture>
      </div>
    );
  }
  return (
    <div className="offer-card-art offer-card-art--catalog" style={scene} aria-hidden="true">
      <OfferCatalogPhoto product={displayProduct} loading={!hydrated || loading} eager={eager} />
    </div>
  );
}

function FirstOrderCardArt({ eager }: { eager: boolean }) {
  const { first_order } = useMoneyRules();
  const product = useOfferGift(first_order.product_id);
  return <CatalogCardArt product={product.data} loading={product.isLoading} eager={eager} />;
}

function BirthdayCardArt({ eager }: { eager: boolean }) {
  const product = useOfferGift(81);
  return <CatalogCardArt product={product.data} loading={product.isLoading} eager={eager} />;
}

function StatusCardArt({ eager }: { eager: boolean }) {
  const { status_rolls } = useMoneyRules();
  const { rolls, isLoading } = useStatusRolls();
  const firstTier = [...status_rolls].sort((a, b) => a.min_sum - b.min_sum)[0]?.type;
  return (
    <CatalogCardArt
      product={firstTier ? rolls[firstTier] : undefined}
      loading={isLoading}
      eager={eager}
    />
  );
}

function DesktopOfferArt({ offer, index }: { offer: PublicPromotion; index: number }) {
  if (offer.detail_key === 'firstOrder') return <FirstOrderCardArt eager={index < 2} />;
  if (offer.detail_key === 'statusRolls') return <StatusCardArt eager={index < 2} />;
  if (offer.detail_key === 'birthday') return <BirthdayCardArt eager={index < 2} />;
  return (
    <div className="offer-card-art" aria-hidden="true">
      <picture>
        <img
          src={photoFor(offer.detail_key)}
          alt=""
          width={1536}
          height={1024}
          loading={index < 2 ? 'eager' : 'lazy'}
          fetchPriority={index === 0 ? 'high' : 'auto'}
        />
      </picture>
    </div>
  );
}

export function ActionsExperience({
  initialPromotions,
  introPreview,
}: {
  initialPromotions: PublicPromotion[] | null;
  introPreview?: IntroOption;
}) {
  const query = useQuery({
    queryKey: ['storefront-promotions'],
    queryFn: async () => {
      const { data } = await api.get<unknown>('/content/promotions?platform=web');
      const result = normalizePromotions(data);
      if (result === null) throw new Error('Не удалось загрузить акции');
      return result;
    },
    initialData: initialPromotions ?? undefined,
    staleTime: 60_000,
    refetchOnMount: 'always',
    retry: 1,
  });
  const offers = normalizePromotions(query.data) ?? [];

  const [introOption, setIntroOption] = useState<IntroOption>(introPreview || 'compact');
  const [activeId, setActiveId] = useState<number | null>(null);
  const [previewSelection, setPreviewSelection] = useState<OfferPreviewSelection>({});
  const lastTrigger = useRef<HTMLButtonElement | null>(null);
  // Resolve from the current response: an admin edit or deactivation must also
  // update an already-open offer, rather than leave stale terms on screen.
  const active = offers.find((offer) => offer.id === activeId);
  const close = () => {
    setActiveId(null);
  };

  return (
    <>
      <Header />
      <div className={`storefront promotions-page${introPreview ? ' intro-study' : ''}`}>
        <a className="skip-link" href="#offers">
          К акциям
        </a>
        <main className="offers-main" id="offers" tabIndex={-1}>
          <div className="offers-container">
            {introPreview ? (
              <>
                <IntroOptionsToolbar
                  value={introOption}
                  onChange={(option) => {
                    setIntroOption(option);
                    const url = new URL(window.location.href);
                    url.searchParams.set('style', option);
                    window.history.replaceState(window.history.state, '', url);
                  }}
                />
                <OfferIntroOption
                  variant={introOption}
                  offers={offers}
                  onOpen={(offer, trigger) => {
                    lastTrigger.current = trigger;
                    setPreviewSelection({});
                    setActiveId(offer.id);
                  }}
                />
              </>
            ) : (
              <section className="offers-selected-intro" aria-labelledby="offers-title">
                <CompactOffersIntro count={offers.length} />
              </section>
            )}

            {query.isError && (
              <div className="offers-notice" role="status">
                <p>
                  {offers.length
                    ? 'Не получилось обновить акции. Перед заказом проверьте условия.'
                    : 'Не получилось загрузить акции. Попробуйте ещё раз.'}
                </p>
                <button onClick={() => void query.refetch()} disabled={query.isFetching}>
                  {query.isFetching ? 'Обновляем…' : 'Попробовать снова'}
                </button>
              </div>
            )}

            {query.isPending && (
              <div className="offers-loading" role="status">
                Загружаем предложения…
              </div>
            )}

            {offers.length > 0 && (
              <div className="offers-grid">
                {offers.map((offer, index) => {
                  const interactive = hasDetails(offer.detail_key);
                  const shape = index % 4;
                  const catalogGift = ['firstOrder', 'birthday', 'statusRolls'].includes(
                    offer.detail_key || ''
                  );
                  return (
                    <article
                      className={`offer-card offer-card--${shape} offer-card--${photoKeys[offer.slug] || 'loyalty'}${interactive ? ' offer-card--interactive' : ''}${catalogGift ? ' offer-card--catalog-gift' : ''}${offer.detail_key === 'statusRolls' ? ' offer-card--catalog-status' : ''}`}
                      key={offer.id}
                      data-offer-slug={offer.slug}
                    >
                      <DesktopOfferArt offer={offer} index={index} />
                      <div className="offer-card-copy">
                        <h2 className="offer-card-heading">{offer.title}</h2>
                        <p className="offer-card-description">{offer.description}</p>
                      </div>
                      {interactive && (
                        <button
                          className="offer-card-open"
                          aria-label={`Условия акции «${offer.title}»`}
                          aria-haspopup="dialog"
                          onClick={(event) => {
                            lastTrigger.current = event.currentTarget;
                            setPreviewSelection({});
                            setActiveId(offer.id);
                          }}
                        >
                          <span className="offer-card-action-label">
                            {offerActionLabels[offer.detail_key || ''] || 'Смотреть предложение'}
                          </span>
                          <span className="offer-card-arrow">
                            <ArrowUpRight size={25} weight="bold" aria-hidden="true" />
                          </span>
                        </button>
                      )}
                    </article>
                  );
                })}
              </div>
            )}

            {offers.length > 0 && (
              <MobileOffersExperience
                offers={offers}
                onOpen={(offer, trigger, selection) => {
                  lastTrigger.current = trigger;
                  setPreviewSelection(selection ?? {});
                  setActiveId(offer.id);
                }}
              />
            )}

            {!query.isPending && !query.isError && offers.length === 0 && (
              <section className="offers-empty">
                <div>
                  <p className="offers-eyebrow">Загляните чуть позже</p>
                  <h2>
                    Новые поводы
                    <br />
                    уже на подходе.
                  </h2>
                  <p>Сейчас активных акций нет. А любимые роллы всегда можно найти в меню.</p>
                  <Link href="/#menu" className="offers-text-link">
                    Перейти в меню <ArrowUpRight size={22} />
                  </Link>
                </div>
                <img src="/storefront/offers-v2-loyalty.webp" alt="" width={1536} height={1024} />
              </section>
            )}

            <section className="offers-faq" id="offers-faq" aria-labelledby="offers-faq-title">
              <div className="offers-faq-intro">
                <p className="offers-eyebrow">Об акциях</p>
                <h2 id="offers-faq-title">
                  Вопросы
                  <br />
                  <span>и ответы.</span>
                </h2>
                <Link href="/faq" className="offers-text-link">
                  Больше ответов <ArrowUpRight size={19} aria-hidden="true" />
                </Link>
              </div>
              <div className="offers-questions">
                <details>
                  <summary>
                    Как воспользоваться акцией?
                    <PlusIcon size={22} aria-hidden="true" />
                  </summary>
                  <p>
                    Откройте условия выбранного предложения: там указано, как получить подарок или
                    бонус. Затем выберите блюда в меню и проверьте состав заказа и итоговую сумму в
                    корзине.
                  </p>
                </details>
                <details>
                  <summary>
                    Можно совместить несколько акций?
                    <PlusIcon size={22} aria-hidden="true" />
                  </summary>
                  <p>
                    У каждой акции свои правила. Совместимость с другими предложениями и исключения
                    указаны в её условиях. Перед оплатой проверьте, какие подарки и скидки
                    применились к заказу.
                  </p>
                </details>
                <details>
                  <summary>
                    Куда вводить промокод?
                    <PlusIcon size={22} aria-hidden="true" />
                  </summary>
                  <p>
                    При оформлении заказа откройте блок «У меня есть промокод», введите код и
                    примените его. Если у предложения есть ограничения, они будут указаны в условиях
                    акции.
                  </p>
                </details>
              </div>
            </section>

            <OfferMenuInvitation />
          </div>
        </main>
        <OfferDialog
          offer={active && hasDetails(active.detail_key) ? active : null}
          onClose={close}
          triggerRef={lastTrigger}
          initialStatusTier={previewSelection.tier}
          initialLoyaltyLevel={previewSelection.level}
        />
      </div>
      <Footer />
    </>
  );
}
